/**
 * IFRAP-AI.fr - Knowledge Base & Book Upload Page
 * Browse books, infographics, style profiles. Upload new books.
 */

import React, { useState, useEffect, useRef } from 'react';
import api from '../../api/client';
import { 
  BookOpen, Upload, Search, BarChart3, FileText, ExternalLink, 
  ChevronDown, ChevronRight, RefreshCw, PenTool, X
} from 'lucide-react';

const KnowledgePage = () => {
  const [books, setBooks] = useState([]);
  const [infographics, setInfographics] = useState([]);
  const [styleProfile, setStyleProfile] = useState(null);
  const [selectedInfographic, setSelectedInfographic] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadAuthor, setUploadAuthor] = useState('');
  const fileInputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [booksRes, infosRes] = await Promise.all([
        api.get('/api/knowledge/books'),
        api.get('/api/knowledge/infographics'),
      ]);
      setBooks(booksRes.data.books || []);
      setInfographics(infosRes.data.infographics || []);
    } catch (err) {
      console.error('Error loading knowledge base:', err);
    }

    // Load style profile
    try {
      const styleRes = await api.get('/api/knowledge/style/agnes_verdier_molinie');
      setStyleProfile(styleRes.data.profile);
    } catch (err) {
      // Style profile not available - non-blocking
    }
  };

  const loadChapters = async (bookId) => {
    try {
      const res = await api.get(`/api/knowledge/books/${bookId}/chapters`);
      setChapters(res.data.chapters || []);
    } catch (err) {
      console.error('Error loading chapters:', err);
    }
  };

  const handleUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file || !uploadTitle || !uploadAuthor) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', uploadTitle);
      formData.append('author', uploadAuthor);
      const res = await api.post('/api/knowledge/books/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        setShowUpload(false);
        setUploadTitle('');
        setUploadAuthor('');
        loadData();
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery) return;
    try {
      const res = await api.get('/api/knowledge/search', { params: { query: searchQuery } });
      setSearchResults(res.data);
    } catch (err) {
      console.error('Search error:', err);
    }
  };

  // Group infographics by type
  const infographicsByType = {};
  infographics.forEach(ig => {
    if (!infographicsByType[ig.type]) infographicsByType[ig.type] = [];
    infographicsByType[ig.type].push(ig);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono text-[#00FF88]">Base de Connaissances</h1>
          <p className="text-gray-400 text-sm mt-1">
            Livres, infographies, profils de style — {books.length} livre(s), {infographics.length} infographie(s)
          </p>
        </div>
        <button
          onClick={() => setShowUpload(!showUpload)}
          className="flex items-center gap-2 px-4 py-2 bg-[#00FF88] text-black font-medium rounded-lg hover:bg-[#00FF88]/90 text-sm"
          data-testid="upload-book-btn"
        >
          <Upload className="w-4 h-4" /> Uploader un livre
        </button>
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <div className="bg-[#1a1a2e] border border-[#00FF88]/30 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-medium text-[#00FF88]">Uploader un nouveau livre</h2>
            <button onClick={() => setShowUpload(false)} className="text-gray-500 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Titre du livre *</label>
              <input
                type="text"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                className="w-full px-3 py-2 bg-black/30 border border-gray-700 rounded text-white text-sm"
                data-testid="upload-book-title"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Auteur *</label>
              <input
                type="text"
                value={uploadAuthor}
                onChange={(e) => setUploadAuthor(e.target.value)}
                className="w-full px-3 py-2 bg-black/30 border border-gray-700 rounded text-white text-sm"
                data-testid="upload-book-author"
              />
            </div>
          </div>
          <div className="mt-3">
            <label className="text-xs text-gray-500 block mb-1">Fichier PDF *</label>
            <input
              type="file"
              accept=".pdf"
              ref={fileInputRef}
              className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-[#00FF88] file:text-black hover:file:bg-[#00FF88]/90"
              data-testid="upload-book-file"
            />
          </div>
          <button
            onClick={handleUpload}
            disabled={uploading || !uploadTitle || !uploadAuthor}
            className="mt-4 w-full py-2.5 bg-[#00FF88] text-black font-medium rounded-lg hover:bg-[#00FF88]/90 disabled:opacity-50 text-sm"
            data-testid="upload-book-submit"
          >
            {uploading ? 'Extraction et indexation...' : "Uploader et indexer"}
          </button>
        </div>
      )}

      {/* Search */}
      <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Rechercher dans la base de connaissances..."
            className="flex-1 px-3 py-2 bg-black/30 border border-gray-700 rounded text-white text-sm"
            data-testid="knowledge-search-input"
          />
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-[#000091] text-white rounded text-sm hover:bg-[#000091]/80"
          >
            <Search className="w-4 h-4" />
          </button>
        </div>
        {searchResults && (
          <div className="mt-3 space-y-2">
            <p className="text-xs text-gray-500">{searchResults.total} resultat(s)</p>
            {searchResults.results?.map((r, i) => (
              <div key={i} className="flex items-center gap-3 text-sm bg-black/20 rounded p-2">
                {r.type === 'infographic' ? <BarChart3 className="w-4 h-4 text-[#FFB000]" /> : <FileText className="w-4 h-4 text-[#00FF88]" />}
                <span className="text-white">{r.title}</span>
                <span className="text-xs text-gray-500 ml-auto">p.{r.page}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Books */}
        <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-5">
          <h2 className="text-sm font-medium text-gray-400 mb-4 flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-[#FFB000]" />
            Livres en base ({books.length})
          </h2>
          {books.length === 0 ? (
            <p className="text-gray-500 text-sm">Aucun livre. Uploadez votre premier PDF.</p>
          ) : (
            <div className="space-y-3">
              {books.map((book) => (
                <div key={book.id} className="bg-black/20 rounded-lg p-4 border border-gray-700/50">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-white font-medium">{book.title}</h3>
                      <p className="text-gray-400 text-sm">{book.author} ({book.year || ''})</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        <span>{book.total_pages || '?'} pages</span>
                        {book.isbn && <span>ISBN: {book.isbn}</span>}
                        <span className={`px-1.5 py-0.5 rounded ${book.status === 'indexed' ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
                          {book.status === 'indexed' ? 'Indexe' : book.status}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => loadChapters(book.id)}
                      className="text-[#00FF88] text-xs hover:underline"
                    >
                      Chapitres
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {chapters.length > 0 && (
            <div className="mt-4 border-t border-gray-700 pt-4">
              <h3 className="text-xs text-gray-400 mb-2">Chapitres</h3>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {chapters.map((ch, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="text-[#FFB000] w-6">{ch.chapter_num}</span>
                    <span className="text-white">{ch.title}</span>
                    <span className="text-gray-600 ml-auto">p.{ch.start_page}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Style Profile */}
        <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-5">
          <h2 className="text-sm font-medium text-gray-400 mb-4 flex items-center gap-2">
            <PenTool className="w-4 h-4 text-[#E1000F]" />
            Profil de style - Imitation IA
          </h2>
          {styleProfile ? (
            <div>
              <div className="bg-black/20 rounded-lg p-4 border border-gray-700/50 mb-4">
                <h3 className="text-white font-medium">{styleProfile.author}</h3>
                <p className="text-gray-400 text-sm">{styleProfile.book}</p>
              </div>

              <h4 className="text-xs text-[#00FF88] mb-2">Caracteristiques du style</h4>
              <ul className="space-y-1 mb-4">
                {styleProfile.characteristics?.map((c, i) => (
                  <li key={i} className="text-xs text-gray-300 flex gap-2">
                    <span className="text-[#FFB000]">-</span> {c}
                  </li>
                ))}
              </ul>

              <h4 className="text-xs text-[#E1000F] mb-2">Phrases signature</h4>
              <div className="space-y-1">
                {styleProfile.sample_phrases?.map((p, i) => (
                  <p key={i} className="text-xs text-gray-400 italic pl-3 border-l border-[#E1000F]/30">
                    "{p}"
                  </p>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-sm">Aucun profil de style disponible.</p>
          )}
        </div>
      </div>

      {/* Infographics */}
      <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-5">
        <h2 className="text-sm font-medium text-gray-400 mb-4 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-[#00FFFF]" />
          Infographies extraites ({infographics.length})
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {infographics.map((ig) => (
            <div
              key={ig.id}
              className={`bg-black/20 rounded-lg p-4 border cursor-pointer transition-all ${
                selectedInfographic === ig.id
                  ? 'border-[#00FFFF] bg-[#00FFFF]/5'
                  : 'border-gray-700/50 hover:border-gray-600'
              }`}
              onClick={() => setSelectedInfographic(selectedInfographic === ig.id ? null : ig.id)}
              data-testid={`infographic-card-${ig.id}`}
            >
              <div className="flex items-start justify-between mb-2">
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                  ig.type === 'table' ? 'bg-blue-500/10 text-blue-400' :
                  ig.type === 'bar_line' ? 'bg-green-500/10 text-green-400' :
                  ig.type === 'waterfall' ? 'bg-purple-500/10 text-purple-400' :
                  ig.type === 'stacked_bar' ? 'bg-orange-500/10 text-orange-400' :
                  'bg-gray-500/10 text-gray-400'
                }`}>
                  {ig.type}
                </span>
                <span className="text-[10px] text-gray-600">p.{ig.page}</span>
              </div>
              <h3 className="text-sm text-white font-medium mb-1">{ig.title}</h3>
              {ig.subtitle && <p className="text-xs text-gray-400">{ig.subtitle}</p>}
              <p className="text-[10px] text-gray-500 mt-2">{ig.source}</p>

              {/* Expanded detail */}
              {selectedInfographic === ig.id && ig.data && (
                <div className="mt-3 pt-3 border-t border-gray-700">
                  <h4 className="text-xs text-[#00FFFF] mb-2">Donnees structurees</h4>
                  <pre className="text-[10px] text-gray-400 overflow-x-auto max-h-40 bg-black/30 rounded p-2">
                    {JSON.stringify(ig.data, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default KnowledgePage;
